import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ParticipantsList } from '@/components/chat/ParticipantsSidebar';
import { useLang } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Copy, Share2, UserPlus } from 'lucide-react';

interface MobileParticipantsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  participants: string[];
  count: number;
  myName: string;
  dmTarget?: string;
  onSelectDm: (name: string) => void;
  onInvite: () => void;
  onShareLink: () => void;
  onCopyLink: () => void;
  roomIsFull?: boolean;
  maxUsers?: number;
}

export function MobileParticipantsSheet({
  open,
  onOpenChange,
  participants,
  count,
  myName,
  dmTarget,
  onSelectDm,
  onInvite,
  onShareLink,
  onCopyLink,
  roomIsFull = false,
  maxUsers = 8,
}: MobileParticipantsSheetProps) {
  const { t } = useLang();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="z-[60] w-[85vw] max-w-xs p-0 flex flex-col bg-sidebar/95 backdrop-blur-xl border-border md:hidden"
      >
        <SheetHeader className="relative p-4 pr-14 border-b border-border shrink-0 text-left space-y-0">
          <SheetTitle className="flex min-w-0 items-center gap-2 text-sm font-medium text-sidebar-foreground">
            <span className="truncate">{t('sidebar.inRoom')}</span>
            <span className="ml-auto shrink-0 bg-sidebar-accent text-sidebar-accent-foreground text-xs px-2 py-0.5 rounded-full font-medium">
              {Math.min(count, maxUsers)}/{maxUsers}
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex items-center justify-center gap-1.5 border-b border-border px-3 py-2 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onInvite}
            disabled={roomIsFull}
            className="h-9 w-9 rounded-full text-sidebar-foreground hover:bg-sidebar-accent"
            aria-label="Inviter un contact"
            title={roomIsFull ? `Room complète (${maxUsers}/${maxUsers})` : 'Inviter un contact'}
          >
            <UserPlus className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onShareLink}
            className="h-9 w-9 rounded-full text-sidebar-foreground hover:bg-sidebar-accent"
            aria-label="Partager le lien"
            title={roomIsFull ? `Room complète (${maxUsers}/${maxUsers})` : 'Partager le lien'}
          >
            <Share2 className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onCopyLink}
            className="h-9 w-9 rounded-full text-sidebar-foreground hover:bg-sidebar-accent"
            aria-label="Copier le lien"
            title="Copier le lien"
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          <ParticipantsList
            participants={participants}
            myName={myName}
            dmTarget={dmTarget}
            onSelectDm={onSelectDm}
          />
        </div>

        <div className="p-4 text-xs text-sidebar-foreground/40 border-t border-border mt-auto font-medium text-center shrink-0">
          {t('sidebar.footer')}
        </div>
      </SheetContent>
    </Sheet>
  );
}
